import {PolymerElement, html} from '@polymer/polymer/polymer-element.js';
import '@polymer/paper-dialog/paper-dialog.js';
import '@polymer/paper-input/paper-input.js';
import '@polymer/paper-dropdown-menu/paper-dropdown-menu.js';
import '@polymer/gold-cc-expiration-input/gold-cc-expiration-input.js';
import '@polymer/paper-button/paper-button.js';
import '@cbar/cbar-shared-styles/cbar-shared-styles.js';
import '@polymer/paper-listbox/paper-listbox.js';
import '@polymer/paper-item/paper-item.js';
import '@cbar/localizemixin/LocalizeMixin.js';
import '@polymer/paper-checkbox/paper-checkbox.js';
import '@polymer/paper-spinner/paper-spinner.js';
import './cbar-paymentmethod-form.js';

/**
 * `cbar-paymentmethod-modal` is an element that allows for users to add payment methods to their currently logged in user
 * it is able to pass in values from external data, and extract the information into a form if there is any that exists, however
 * if there is no context then we assume that we are adding a new payment method instead of editing an existing one
 *
 *
 * @customElement
 * @polymer
 * @demo demo/index.html
 */
class CbarPaymentmethodModal extends Cbar.LocalizeMixin(PolymerElement) {
  static get template() {
    return html`
        <style include="cbar-shared-styles">
            :host {
                display: block;
            }

            hr {
                padding: 0;
                border: 0.5px solid #eeeeee
            }

            .update-button {
                background-color: white;
                width: 10%;
            }

            .cancel-button {
                color: red;
            }

            .actions {
                float: right;
                margin-top: 0;
                margin-bottom: 5px;
            }

            .payment-type-details {
                width: 45%;
                display: inline-block;
            }

            [hidden] {
                display: none;
            }

            paper-button[disabled] {
                color: grey;
            }

            paper-input, gold-cc-expiration-input {
                --paper-input-container-underline-focus: {
                    border-color: var(--cbar-sys-color-primary, #004CA2);
                }
            }

            paper-checkbox {
                margin-top: 16px;
            }
        </style>
        <paper-dialog id="dialog" with-backdrop opened="{{opened}}" no-cancel-on-esc-key no-cancel-on-outside-click scroll-action="lock">
            <cbar-paymentmethod-form cvv-required$="[[cvvRequired]]" modal-action="[[modalAction]]" payment-methods="[[paymentMethods]]" context="{{context}}" payment-data="{{paymentData}}" hide-preferred$="[[hidePreferred]]"  hide-context$="[[hideContext]]" hide-card-name$="[[hideCardName]]" hide-institution=$"[[hideInstitution]]" hide-account-owner$="[[hideAccountOwner]]" loading$="[[loading]]" allowed-contexts="[[allowedContexts]]"></cbar-paymentmethod-form>
            <div class="buttons" style="display: flex;align-items: center; justify-content: space-between">
                <div>
                    <template is="dom-if" if="[[errorMessage]]">
                        <div style="color: red">[[errorMessage]]</div>
                    </template>
                </div>
                <div style="display: flex;align-items: center">
                    <paper-spinner active$="[[loading]]"></paper-spinner>
                    <paper-button class="cancel-button" dialog-dismiss disabled$="[[loading]]"></paper-button>
                    <paper-button class="update-button" on-click="_handleUpdated" disabled$="[[loading]]"></paper-button>
                </div>
            </div>
        </paper-dialog>
    `;
  }

  static get is() { return 'cbar-paymentmethod-modal'; }

  static get properties() {
    return {
      paymentMethods: {
        type: Array,
        value: []
      },
      /**
       *  Property that controls the context of the modal, changes the "adding" name on the top of the modal and controls
       *  the inputs that will show.
       */
      addContext: {
        type: String
      },
      /**
       * Property that calls the open function, when it is true, it will trigger the dialog and open the modal
       */
      opened: {
        type: Boolean,
        notify: true
      },
      /**
       * Property that controls the type of modal that will show, if the user gives context then it will assume there is something
       * to be updated, if not then we assume that we are adding a new payment method.
       */
      modalAction: {
        type: String,
        value: 'Add',
      },
      /**
       * Boolean that controls if we are adding a credit card or not, if so, then it will show the correct inputs for carrying
       * out such a form
       */
      _addingCredit: {
        type: Boolean
      },
      /**
       * Similar to `addingCredit`, however controls if the user is seeing the adding of a debit payment form.
       */
      _addingDebit: {
        type: Boolean
      },
      /**
       * Is the imported data, is the starting point of the element and will call on `_checkPaymentType` when it is entered into the element
       */
      paymentData: {
        type: Object,
        notify: true,
      },
      context: {
        type: String,
        value: this.initialContext,
        observer: '_resizeModal'
      },
      hidePreferred: {
        type: Boolean,
        value: false
      },
      hideCardName: {
        type: Boolean,
        value: false
      },
      hideInstitution: {
        type: Boolean,
        value: false
      },
      hideAccountOwner: {
        type: Boolean,
        value: false
      },
      loading: {
        type: Boolean,
        value: false
      },
      errorMessage: {
        type: String,
        value: ""
      },
      allowedContexts: {
        type: Array,
        value: [
          {
            value: "card",
            title: "Credit Card"
          },
          {
            value: "debit",
            title: "Debit"
          }
        ]
      },
      cancelWording: {
        type: String,
        value: "Cancel",
        observer: "__handleCancelButtonWording"
      },
      updateWording: {
        type: String,
        value: "Add",
        observer: "__handleUpdateButtonWording"
      },
      hideContext : {
        type: Boolean,
        value: false
      },
      cvvRequired: {
        type: Boolean,
        value: false
      }
    };
  }

  static get initialContext() {
    return 'Payment Method';
  }

  get form() {
    return this.root.querySelector('cbar-paymentmethod-form');
  }

  get cancelButton() {
    return this.root.querySelector('.cancel-button');
  }

  get updateButton() {
    return this.root.querySelector('.update-button');
  }


  reset() {
    this.context = this.constructor.initialContext;
    this.resetData();
  }

  resetData() {
    this.form.paymentData = {};
  }

  /**
   * Allows for the resizing of the modal, uses an inbuilt function of the `paper-datatable-api` element which determines
   * the width required for it to sit in the middle of the screen correctly.
   */
  _resizeModal() {
    requestAnimationFrame(() => {
      this.$.dialog.refit();
    });
  }

  async __handleCancelButtonWording(word) {
    if (!word) {
      return;
    }

    this.cancelButton.innerHTML = await this.localize(word);
  }

  async __handleUpdateButtonWording(word) {
    if (!word) {
      return;
    }

    this.updateButton.innerHTML = await this.localize(word);
  }

  /**
   * Current function that allows for the expiry date to display correctly, as the `cc-expiry-date` element has issues with
   * loading correctly.
   *
   * @param expiry
   * @returns {string}
   * @private
   */
  _computeExpiry(expiry) {
    if (expiry) {
      requestAnimationFrame(() => {
        if (this.root.querySelector('#cardExpiry')) {
          this.root.querySelector('#cardExpiry').value = expiry;
        }
      });
    }

    // Always return an empty string, then on the next animation frame
    // the expiry will be set by our manual querySelector.
    return '';
  }

  /**
   * general validation function
   * @return {boolean}
   */
  validate() {
    return this.form.validate();
  }

  _handleUpdated() {
    if (!this.validate()) {
      return;
    }

    let data = {...this.paymentData};

    if (this.form.expiryInput) {
      data.expiry = this.form.expiryInput.value
    }

    this.dispatchEvent(new CustomEvent('update-clicked', {
      bubbles: true,
      composed: true,
      detail: {
        value: data
      }
    }))
  }

  toggle() {
    this.$.dialog.toggle();
  }
}

window.customElements.define(CbarPaymentmethodModal.is, CbarPaymentmethodModal);
